import { Note, NoteType } from '../utils/note'

export type SearchResult = {
  recordName: string
  title: string
  filename: string
  noteType: NoteType
  matchedLines: MatchedLine[]
  score: number
}

export type MatchedLine = {
  content: string
  lineIndex: number
}

/**
 * Search for content within a single note
 */
function searchContent(query: string, note: Note): MatchedLine[] {
  const results: MatchedLine[] = []
  const content = note.content

  if (!note.recordName || query.length <= 1 || !content) {
    return results
  }

  const lowerQuery = query.toLowerCase()
  // Split query into individual terms for OR search (case-insensitive)
  const queryTerms = lowerQuery.split(/\s+or\s+/i).map((term) => term.trim())
  
  const lines = content.split('\n')
  
  for (const [index, line] of lines.entries()) {
    const lowerLine = line.toLowerCase()
    // Check if any of the query terms match this line
    if (queryTerms.some((term) => lowerLine.includes(term))) {
      results.push({
        content: line,
        lineIndex: index,
      })
    }
  }

  return results
}

/**
 * Calculate relevance score for a search result
 */
function calculateScore(
  query: string,
  note: Note,
  matchedLines: MatchedLine[]
): number {
  const lowerQuery = query.toLowerCase()
  // Split query into individual terms for OR search
  const queryTerms = lowerQuery.split(/\s+or\s+/i).map((term) => term.trim())
  
  let score = 0
  
  // Title match is weighted higher
  const titleLower = note.title?.toLowerCase() || ''
  if (queryTerms.some((term) => titleLower.includes(term))) {
    score += 10
  }
  
  // Each matched line adds to the score
  score += matchedLines.length * 2
  
  // Exact matches get bonus points
  for (const line of matchedLines) {
    const lineLower = line.content.toLowerCase()
    if (queryTerms.some((term) => lineLower === term)) {
      score += 5
    }
  }
  
  return score
}

/**
 * Search through multiple note maps
 */
export function searchNotes(
  query: string,
  noteMaps: {
    privateProjectNotes?: Map<string, Note>
    privateCalendarNotes?: Map<string, Note>
    teamProjectNotes?: Map<string, Note>
    teamCalendarNotes?: Map<string, Note>
  },
  isInSpecialFolder: (recordName: string | undefined) => boolean,
  limit = 20
): SearchResult[] {
  const results: SearchResult[] = []
  
  if (query.length <= 1) {
    return results
  }
  
  // Combine all note maps
  const allNotes: Note[] = []
  
  if (noteMaps.privateProjectNotes) {
    allNotes.push(...noteMaps.privateProjectNotes.values())
  }
  if (noteMaps.privateCalendarNotes) {
    allNotes.push(...noteMaps.privateCalendarNotes.values())
  }
  if (noteMaps.teamProjectNotes) {
    allNotes.push(...noteMaps.teamProjectNotes.values())
  }
  if (noteMaps.teamCalendarNotes) {
    allNotes.push(...noteMaps.teamCalendarNotes.values())
  }
  
  // Search through all notes
  for (const note of allNotes) {
    // Skip notes in special folders
    if (isInSpecialFolder(note.recordName)) {
      continue
    }
    
    // Skip team space root notes
    if (note.noteType === NoteType.TEAM_SPACE) {
      continue
    }
    
    // Skip folders
    if (note.isFolder) {
      continue
    }
    
    const matchedLines = searchContent(query, note)
    
    // Check title match even if no content matches
    const lowerQuery = query.toLowerCase()
    const queryTerms = lowerQuery.split(/\s+or\s+/i).map((term) => term.trim())
    const titleMatches = queryTerms.some(
      (term) => note.title?.toLowerCase().includes(term) || false
    )
    
    if (matchedLines.length > 0 || titleMatches) {
      const score = calculateScore(query, note, matchedLines)
      
      results.push({
        recordName: note.recordName ?? '',
        title: note.title ?? note.filename,
        filename: note.filename,
        noteType: note.noteType,
        matchedLines: matchedLines.slice(0, 5), // Limit matched lines per note
        score,
      })
    }
  }
  
  // Sort by relevance score (highest first)
  results.sort((a, b) => b.score - a.score)
  
  // Return limited results
  return results.slice(0, limit)
}

/**
 * Format search results for AI consumption
 */
export function formatSearchResultsForAI(results: SearchResult[]): string {
  if (results.length === 0) {
    return 'No notes found matching the search query.'
  }
  
  const formatted = results.map((result, index) => {
    const excerpts = result.matchedLines
      .slice(0, 3)
      .map(line => `  - Line ${String(line.lineIndex + 1)}: ${line.content.slice(0, 150)}`)
      .join('\n')
    
    return `${String(index + 1)}. "${result.title}" (${result.filename})
${excerpts.length > 0 ? excerpts : '  - Title match only'}`
  }).join('\n\n')
  
  return `Found ${String(results.length)} matching notes:\n\n${formatted}`
}

/**
 * Search notes implementation for API usage
 */
export function searchNotesForAPI(
  query: string,
  limit: number,
  notesData: {
    privateProjectNotes?: [string, Note][]
    privateCalendarNotes?: [string, Note][]
    teamProjectNotes?: [string, Note][]
    teamCalendarNotes?: [string, Note][]
  }
): { results: SearchResult[]; summary: string } {
  // Convert arrays back to Maps
  const noteMaps = {
    privateProjectNotes: notesData.privateProjectNotes 
      ? new Map(notesData.privateProjectNotes) 
      : undefined,
    privateCalendarNotes: notesData.privateCalendarNotes
      ? new Map(notesData.privateCalendarNotes)
      : undefined,
    teamProjectNotes: notesData.teamProjectNotes
      ? new Map(notesData.teamProjectNotes)
      : undefined,
    teamCalendarNotes: notesData.teamCalendarNotes
      ? new Map(notesData.teamCalendarNotes)
      : undefined,
  }
  
  // Simple isInSpecialFolder check
  const isInSpecialFolder = (recordName: string | undefined) => {
    if (!recordName) return false
    
    // Check all notes to see if this note is inside a special folder
    const allNotes = [
      ...(noteMaps.privateProjectNotes?.values() ?? []),
      ...(noteMaps.privateCalendarNotes?.values() ?? []),
      ...(noteMaps.teamProjectNotes?.values() ?? []),
      ...(noteMaps.teamCalendarNotes?.values() ?? []),
    ]
    
    // Find special folders
    const specialFolders = allNotes.filter(
      n => n.filename.startsWith('@') && n.isFolder && n.recordName
    )
    
    // Check if the note is a descendant of any special folder
    const checkIfDescendant = (noteId: string): boolean => {
      for (const note of allNotes) {
        if (note.recordName === noteId && note.parent && (specialFolders.some(sf => sf.recordName === note.parent) || checkIfDescendant(note.parent))) {
          return true
        }
      }
      return false
    }
    
    return specialFolders.some(sf => sf.recordName === recordName) || checkIfDescendant(recordName)
  }
  
  const results = searchNotes(query, noteMaps, isInSpecialFolder, limit)
  const summary = formatSearchResultsForAI(results)
  
  return { results, summary }
}